import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';

const APP_VERSION = "3.0";

// Morse Code Generator
const MORSE_CODE = {
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
  'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
  'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
  'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
  'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
  '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
  '8': '---..', '9': '----.', ' ': ' ', '-': '-....-', '.': '.-.-.-'
};

// Messages morse variés selon le contexte
// RÈGLES MARINE FRANÇAISE:
// - Tous les officiers sont appelés "COMMANDANT"
// - Tous les amiraux sont appelés "AMIRAL"
// Surnoms: Corvettard (CC), Frégaton (CF), Cap-de-vau (CV), Loufiat (LV), Midship (ASP)

const MARINE_GRADES = {
  // Amiraux - tous appelés "AMIRAL"
  'Amiral': { appellation: 'AMIRAL', surnom: 'AMIRAL' },
  'Vice-amiral d\'escadre': { appellation: 'AMIRAL', surnom: 'AMIRAL' },
  'Vice-amiral': { appellation: 'AMIRAL', surnom: 'AMIRAL' },
  'Contre-amiral': { appellation: 'AMIRAL', surnom: 'AMIRAL' },
  
  // Officiers supérieurs - tous appelés "COMMANDANT"
  'Capitaine de vaisseau': { appellation: 'COMMANDANT', surnom: 'CAP DE VAU' },
  'Capitaine de frégate': { appellation: 'COMMANDANT', surnom: 'FREGATON' },
  
  // Officiers subalternes - appelés "COMMANDANT" aussi
  'Lieutenant de vaisseau': { appellation: 'COMMANDANT', surnom: 'LOUFIAT' },
  'Enseigne de vaisseau de 1ère classe': { appellation: 'COMMANDANT', surnom: 'ENSEIGNE' },
  'Enseigne de vaisseau de 2ème classe': { appellation: 'COMMANDANT', surnom: 'ENSEIGNE' },
  'Aspirant': { appellation: 'COMMANDANT', surnom: 'MIDSHIP' },
  
  // Grades Terre / Gendarmerie
  "Chef d'escadron": { appellation: 'COMMANDANT', surnom: 'CEN' },
  'Commandant': { appellation: 'COMMANDANT', surnom: 'CDT' },
  'Commandant (R)': { appellation: 'COMMANDANT', surnom: 'CDT (R)' },
  'Colonel': { appellation: 'COLONEL', surnom: 'COLONEL' },
  'Lieutenant-colonel': { appellation: 'COLONEL', surnom: 'LCL' },

  // Fallback
  'Officier': { appellation: 'COMMANDANT', surnom: 'COMMANDANT' }
};

const getMarineAppellation = (rankTitle) => {
  // Chercher le grade exact
  if (MARINE_GRADES[rankTitle]) {
    return MARINE_GRADES[rankTitle];
  }
  
  // Chercher par mots-clés
  const rank = (rankTitle || '').toLowerCase();
  if (rank.includes('amiral')) {
    return { appellation: 'AMIRAL', surnom: 'AMIRAL' };
  }
  if (rank.includes('vaisseau') || rank.includes('frégate') || rank.includes('fregate')) {
    return { appellation: 'COMMANDANT', surnom: 'COMMANDANT' };
  }
  
  // Fallback - tout officier est "Commandant"
  return { appellation: 'COMMANDANT', surnom: 'COMMANDANT' };
};

const getMorseMessage = (user, alertCount = 0, newFeatures = false) => {
  const rankTitle = user?.rank_title || 'Officier';
  const { appellation, surnom } = getMarineAppellation(rankTitle);
  const hour = new Date().getHours();
  
  // Salutations avec appellation correcte
  const greetings = [
    `BONJOUR ${appellation}`,
    `BIENVENUE ${appellation}`,
    `A VOS ORDRES ${appellation}`,
    `PRET AU RAPPORT ${appellation}`,
  ];
  
  // Messages avec surnom (plus familier)
  const surnomMessages = [
    `COMMENT ALLEZ VOUS ${surnom}`,
    `SALUT ${surnom}`,
  ];
  
  // Messages selon l'heure
  const timeBasedMessages = [];
  if (hour >= 5 && hour < 12) {
    timeBasedMessages.push(`BON QUART DU MATIN ${appellation}`);
    timeBasedMessages.push(`CAFE SERVI ${appellation}`);
  } else if (hour >= 12 && hour < 14) {
    timeBasedMessages.push(`BON APPETIT ${appellation}`);
  } else if (hour >= 14 && hour < 18) {
    timeBasedMessages.push(`BON QUART ${appellation}`);
  } else if (hour >= 18 && hour < 22) {
    timeBasedMessages.push(`BONNE SOIREE ${appellation}`);
  } else {
    timeBasedMessages.push(`QUART DE NUIT ${appellation}`);
    timeBasedMessages.push(`VEILLE ACTIVE`);
  }
  
  // Messages opérationnels
  const operationalMessages = [
    `SYSTEME OPERATIONNEL`,
    `VEILLE STRATEGIQUE ACTIVE`,
    `OSINT MARINE PRET`,
    `SOURCES EN LIGNE`,
    `SURVEILLANCE ACTIVE`,
    `PARE A VIRER`,
    `EQUIPAGE A POSTE`,
  ];
  
  // Messages contextuels
  const contextMessages = [];
  if (alertCount > 0) {
    contextMessages.push(`${alertCount} ALERTES EN ATTENTE`);
    contextMessages.push(`ALERTES A TRAITER ${appellation}`);
  }
  if (newFeatures) {
    contextMessages.push(`NOUVELLES FONCTIONS V${APP_VERSION}`);
    contextMessages.push(`MISE A JOUR DEPLOYEE`);
  }
  
  // Messages aléatoires variés
  const randomMessages = [
    `SITUATION NORMALE`,
    `RAS A SIGNALER`,
    `TOUS POSTES PARES`,
    `BRANLE BAS DE COMBAT`,
    `MISSION BRIEFING PRET`,
    `DONNEES FRAICHES`,
    `ANALYSE EN COURS`,
    `LA MER EST BELLE`,
    `BON VENT ${appellation}`,
  ];
  
  // Combiner tous les messages possibles
  const allMessages = [
    ...greetings,
    ...surnomMessages,
    ...timeBasedMessages,
    ...operationalMessages,
    ...contextMessages,
    ...randomMessages,
  ];
  
  // Sélectionner aléatoirement
  return allMessages[Math.floor(Math.random() * allMessages.length)];
};

const playMorseCode = (text, audioCtx, onComplete) => {
  try {
    const ctx = audioCtx;
    if (!ctx) {
      if (onComplete) onComplete();
      return;
    }
    
    // S'assurer que le contexte est actif (mobile)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const dotDuration = 0.05;
    const dashDuration = dotDuration * 3;
    const symbolGap = dotDuration;
    const letterGap = dotDuration * 3;
    const wordGap = dotDuration * 7;
    const frequency = 700;
    
    // IMPORTANT: utiliser currentTime FRAIS (pas stale)
    let currentTime = ctx.currentTime + 0.05; // léger offset pour garantir le futur
    
    const playTone = (duration) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      // Enveloppe douce pour éviter les clics audio
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, currentTime + 0.005);
      gainNode.gain.setValueAtTime(0.3, currentTime + duration - 0.005);
      gainNode.gain.linearRampToValueAtTime(0, currentTime + duration);
      
      oscillator.start(currentTime);
      oscillator.stop(currentTime + duration + 0.01);
      
      currentTime += duration + symbolGap;
    };
    
    const upperText = text.toUpperCase();
    
    for (let i = 0; i < upperText.length; i++) {
      const char = upperText[i];
      const morse = MORSE_CODE[char];
      
      if (morse === ' ') {
        currentTime += wordGap;
      } else if (morse) {
        for (const symbol of morse) {
          if (symbol === '.') {
            playTone(dotDuration);
          } else if (symbol === '-') {
            playTone(dashDuration);
          }
        }
        currentTime += letterGap;
      }
    }
    
    if (onComplete) {
      const totalDuration = (currentTime - ctx.currentTime) * 1000;
      setTimeout(onComplete, Math.max(100, totalDuration));
    }
    
    console.log('[MORSE] Playing:', text, '| ctx.state:', ctx.state);
  } catch (error) {
    console.warn('[MORSE] Audio not available:', error.message);
    if (onComplete) onComplete();
  }
};

const BOOT_MESSAGES = [
  { module: 'BIOS', message: 'Initialisation système CESM...', delay: 150 },
  { module: 'SECURE', message: 'Activation mode furtif - Traces désactivées', delay: 150 },
  { module: 'KERNEL', message: `Chargement noyau CESM-OSINT v${APP_VERSION}`, delay: 150 },
  { module: '⚠ PROTO', message: '*** PROTOTYPE / PREUVE DE CONCEPT ***', delay: 150, isWarning: true },
  { module: 'DEV', message: '[05/03/2026] Brief reçu - Amiral BECHLER', delay: 150 },
  { module: 'DEV', message: '[05/03/2026] Codage OSINT-MARINE.FR', delay: 150 },
  { module: 'DEV', message: `Versions déployées: V.01 → V.${APP_VERSION}`, delay: 150 },
  { module: 'METRICS', message: 'Analyse codebase: 41 000+ lignes de code', delay: 150, isDynamic: true },
  { module: 'CRYPTO', message: 'Module cryptographique AES-256-GCM...', delay: 150 },
  { module: 'CRYPTO', message: 'Vérification certificats SSL/TLS...', delay: 150 },
  { module: 'SECURE', message: 'Chiffrement bout-en-bout activé', delay: 150 },
  { module: 'NETWORK', message: 'Configuration réseau sécurisé...', delay: 150 },
  { module: 'NETWORK', message: 'Tunnel chiffré établi...', delay: 150 },
  { module: 'SECURE', message: 'Masquage empreinte numérique - ACTIF', delay: 150 },
  { module: 'NETWORK', message: 'Connexion serveurs OSINT via proxy...', delay: 150 },
  { module: 'DATABASE', message: 'Base de données locale chiffrée - OK', delay: 150 },
  { module: 'AUTH', message: 'Validation credentials...', delay: 150 },
  { module: 'SECURE', message: 'Journalisation anonymisée - OK', delay: 150 },
  { module: 'OSINT', message: 'Chargement modules de scraping furtif...', delay: 150 },
  { module: 'OSINT', message: 'Configuration sources IEA, EIA, GDELT...', delay: 150 },
  { module: 'THEATER', message: 'Chargement théâtres: Ormuz, Polynésie, Mer Rouge...', delay: 150 },
  { module: 'CRISIS', message: 'Module Early Warning System (EWS) - ACTIF', delay: 150 },
  { module: 'SIMUL', message: 'Moteur Monte Carlo calibré (1973-2024)...', delay: 150 },
  { module: 'SECURE', message: 'Rotation automatique des proxies - ACTIF', delay: 150 },
  { module: 'AI', message: 'Connexion IA externe (brief uniquement)...', delay: 150 },
  { module: 'AI', message: 'Analyse finale: TRAITEMENT LOCAL', delay: 150 },
  // === NOUVEAUTES V3.0 ===
  { module: '★ V3.0', message: '═══════ NOUVEAUTÉS V3.0 (IRONSIGHT) ═══════', delay: 200, isNew: true },
  { module: '★ MAP', message: 'Carte interactive Leaflet multi-théâtre initialisée', delay: 150, isNew: true },
  { module: '★ ADSB', message: 'Module ADS-B: suivi aéronefs militaires temps réel (adsb.lol)', delay: 150, isNew: true },
  { module: '★ FIRMS', message: 'Module NASA FIRMS: détection thermique satellite VIIRS', delay: 150, isNew: true },
  { module: '★ TGRAM', message: 'Module Telegram Live: 26 canaux, traduction auto AR/HE/FA', delay: 150, isNew: true },
  { module: '★ AI', message: 'ARGOS (OIA-1) — Officier d\'Intelligence Artificielle activé', delay: 150, isNew: true },
  { module: '★ VALID', message: 'Validateur de données anti-rapports faussés - ACTIF', delay: 150, isNew: true },
  { module: '★ EMAIL', message: 'ARGOS: Questions par email autorisées — analyste@osint-marine.fr', delay: 150, isNew: true },
  // === FIN NOUVEAUTES ===
  { module: 'SECURE', message: 'Effacement mémoire volatile programmé', delay: 150 },
  { module: 'UI', message: `Chargement interface tactique v${APP_VERSION}...`, delay: 150 },
  { module: 'SECURITY', message: 'Activation pare-feu...', delay: 150 },
  { module: 'SECURE', message: 'Anti-forensic measures - ENABLED', delay: 150 },
  { module: 'SYSTEM', message: 'Tous les modules opérationnels - Mode FURTIF', delay: 200 },
];

const ASCII_LOGO = `
 ██████╗███████╗███████╗███╗   ███╗
██╔════╝██╔════╝██╔════╝████╗ ████║
██║     █████╗  ███████╗██╔████╔██║
██║     ██╔══╝  ╚════██║██║╚██╔╝██║
╚██████╗███████╗███████║██║ ╚═╝ ██║
 ╚═════╝╚══════╝╚══════╝╚═╝     ╚═╝
  CENTRE D'ÉTUDES STRATÉGIQUES DE LA MARINE
       SYSTÈME OSINT SÉCURISÉ v${APP_VERSION}
`;

const BootSequence = () => {
  const [messages, setMessages] = useState([]);
  const [progress, setProgress] = useState(0);
  const [showLogo, setShowLogo] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [bootComplete, setBootComplete] = useState(false);
  const [morseTriggered, setMorseTriggered] = useState(false);
  const [systemInitialized, setSystemInitialized] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const audioCtxRef = useRef(null);
  const morseMessageRef = useRef(null);

  // Base font size for boot - MUCH LARGER
  const baseFontSize = "text-lg md:text-xl";

  // Initialiser le système (débloque l'AudioContext via interaction utilisateur)
  const initializeSystem = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;

      // CRITIQUE MOBILE: Produire du son immédiatement dans le handler du clic.
      // Sur iOS/Android, un simple .resume() ne suffit pas — il faut un output audio réel.
      const unlock = ctx.createOscillator();
      const silentGain = ctx.createGain();
      silentGain.gain.value = 0.001; // quasi-inaudible
      unlock.connect(silentGain);
      silentGain.connect(ctx.destination);
      unlock.start(ctx.currentTime);
      unlock.stop(ctx.currentTime + 0.05);

      // Keepalive: empêcher le navigateur de re-suspendre le contexte
      const keepAlive = ctx.createGain();
      keepAlive.gain.value = 0;
      keepAlive.connect(ctx.destination);
      // Un noeud connecté sans source suffit à maintenir le contexte actif

      ctx.resume().then(() => {
        console.log('[MORSE] AudioContext débloqué par interaction + son test');
      });
    } catch (e) {
      console.warn('[MORSE] AudioContext non disponible:', e);
    }
    setSystemInitialized(true);
  };

  // Fonction pour jouer le morse (appelée quand le boot est complet)
  const triggerMorse = async () => {
    if (morseTriggered || !morseMessageRef.current) return;
    
    try {
      const ctx = audioCtxRef.current;
      if (!ctx) return;

      // MOBILE: re-forcer resume avant de jouer (peut avoir été re-suspendu)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      // Double vérification: si toujours suspendu, on tente un dernier resume
      if (ctx.state !== 'running') {
        console.warn('[MORSE] AudioContext still', ctx.state, '— forcing resume');
        await ctx.resume();
      }

      playMorseCode(morseMessageRef.current, ctx);
      setMorseTriggered(true);
      console.log('[MORSE] Joué:', morseMessageRef.current, '(state:', ctx.state, ')');
    } catch (e) {
      console.warn('[MORSE] Erreur audio:', e);
    }
  };

  // Jouer le morse automatiquement quand le boot est complet
  // (AudioContext déjà débloqué par le bouton initializeSystem)
  useEffect(() => {
    if (bootComplete && morseMessageRef.current && !morseTriggered && audioCtxRef.current) {
      triggerMorse();
    }
  }, [bootComplete, morseTriggered]);

  // Auto-scroll effect
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Ne pas démarrer le boot tant que l'utilisateur n'a pas cliqué sur "Initialiser"
    if (!systemInitialized) return;

    // Prevent double execution in StrictMode
    let isCancelled = false;
    const timeouts = [];
    
    const runBootSequence = () => {
      BOOT_MESSAGES.forEach((msg, index) => {
        // Add message with loading status
        const addTimeout = setTimeout(() => {
          if (isCancelled) return;
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.message === msg.message && m.module === msg.module)) {
              return prev;
            }
            return [...prev, { ...msg, status: 'loading' }];
          });
        }, index * 180);
        timeouts.push(addTimeout);
        
        // Update to OK status
        const okTimeout = setTimeout(() => {
          if (isCancelled) return;
          setMessages(prev => 
            prev.map(m => 
              m.message === msg.message && m.module === msg.module 
                ? { ...m, status: 'ok' } 
                : m
            )
          );
          setProgress(Math.round(((index + 1) / BOOT_MESSAGES.length) * 100));
          
          // Complete boot after last message
          if (index === BOOT_MESSAGES.length - 1) {
            setTimeout(async () => {
              if (isCancelled) return;
              setShowLogo(true);
              
              // Récupérer le contexte pour le message morse
              let alertCount = 0;
              try {
                const alertsRes = await axios.get(`${API}/alerts/count`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                alertCount = alertsRes.data?.count || 0;
              } catch (e) {
                // Ignorer les erreurs
              }
              
              // IMPORTANT: Stocker le message morse AVANT de déclencher bootComplete
              // car le useEffect [bootComplete] appelle triggerMorse qui lit morseMessageRef
              const morseMessage = getMorseMessage(user, alertCount, true);
              morseMessageRef.current = morseMessage;
              
              // Maintenant déclencher bootComplete → useEffect → triggerMorse
              setBootComplete(true);
            }, 300);
          }
        }, index * 180 + msg.delay);
        timeouts.push(okTimeout);
      });
    };

    setTimeout(runBootSequence, 300);
    
    return () => {
      isCancelled = true;
      timeouts.forEach(t => clearTimeout(t));
    };
  }, [user, token, navigate, systemInitialized]);

  useEffect(() => {
    if (bootComplete && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (bootComplete && countdown === 0) {
      // Marquer le boot comme complété pour cette session
      sessionStorage.setItem('cesm_boot_completed', 'true');
      navigate('/dashboard');
    }
  }, [bootComplete, countdown, navigate]);

  const progressBar = '▓'.repeat(Math.floor(progress / 5)) + '░'.repeat(20 - Math.floor(progress / 5));

  // Écran de splash — l'utilisateur doit cliquer pour débloquer l'audio
  if (!systemInitialized) {
    return (
      <div className="min-h-screen bg-marine-void flex items-center justify-center font-mono" data-testid="boot-splash">
        <div className="max-w-2xl mx-auto text-center p-8">
          <pre className="text-marine-cyan text-xs sm:text-sm md:text-base leading-tight overflow-x-auto text-glow-cyan font-bold mb-8">
            {ASCII_LOGO}
          </pre>
          
          {user && (
            <p className="text-marine-green text-lg md:text-xl mb-6">
              Bienvenue, {user.rank_title} {user.name}
            </p>
          )}
          
          <p className="text-white/60 text-sm mb-8">
            Session authentifiée — Système prêt
          </p>
          
          <button
            onClick={initializeSystem}
            className="px-8 py-4 bg-marine-cyan/20 border-2 border-marine-cyan text-marine-cyan text-xl font-bold tracking-widest uppercase hover:bg-marine-cyan/30 transition-all duration-300 animate-pulse"
            data-testid="init-system-btn"
          >
            INITIALISER LE SYSTÈME
          </button>
          
          <p className="text-white/30 text-xs mt-6">
            Le signal morse sera joué à la fin de la séquence de démarrage
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-marine-void p-6 md:p-12 font-mono overflow-hidden" data-testid="boot-sequence">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="text-marine-green text-xl md:text-2xl font-bold mb-3">
            CESM - CENTRE D'ÉTUDES STRATÉGIQUES DE LA MARINE v{APP_VERSION}
          </p>
          <p className="text-white/70 text-base md:text-lg">
            Copyright (C) 2026 - LPP Logiciel CDT (R) Jean-Baptiste Giraud | Conseil Strategique: COL (R) Thierry Assonion
          </p>
        </div>

        {/* User Welcome */}
        {user && (
          <div className="mb-8 p-5 border-2 border-marine-cyan/50 bg-marine-cyan/10">
            <p className="text-marine-cyan text-xl md:text-2xl font-bold">
              <span className="text-white/60">[AUTH]</span> Bienvenue, <span className="text-marine-green">{user.rank_title}</span> {user.name}
            </p>
            <p className="text-white/70 text-base md:text-lg mt-2">
              Code Grade: {user.rank_code} | Session authentifiée
            </p>
          </div>
        )}

        {/* Boot Messages - Auto-scroll container */}
        <div 
          ref={scrollRef}
          className="mb-8 h-[350px] overflow-y-auto bg-black/50 p-6 border-2 border-marine-slate/50"
        >
          {messages.map((msg, idx) => (
            <div 
              key={idx} 
              className={`flex items-start gap-3 text-base md:text-lg mb-2 ${msg.isWarning ? 'bg-red-900/30 border border-red-500/50 p-2 -mx-2 rounded' : ''} ${msg.isNew ? 'bg-amber-900/20 border border-amber-500/40 p-2 -mx-2 rounded' : ''}`}
              data-testid={`boot-msg-${idx}`}
            >
              <span className={`font-bold ${msg.isWarning ? 'text-red-500 animate-pulse' : msg.isNew ? 'text-amber-400' : 'text-marine-slate'}`}>
                [{msg.module.padEnd(8)}]
              </span>
              <span className={
                msg.isWarning 
                  ? 'text-red-400 font-bold animate-pulse' 
                  : msg.isNew
                    ? 'text-amber-300 font-bold'
                    : msg.status === 'ok' 
                      ? 'text-white font-bold' 
                      : 'text-marine-cyan/70'
              }>
                {msg.message}
              </span>
              <span className={`ml-auto font-bold ${
                msg.isWarning 
                  ? 'text-red-500 animate-pulse' 
                  : msg.isNew
                    ? 'text-amber-400'
                    : msg.status === 'ok' 
                      ? 'text-marine-green' 
                      : 'text-marine-amber animate-pulse'
              }`}>
                {msg.isWarning ? '[⚠]' : msg.isNew ? '[NEW]' : msg.status === 'ok' ? '[OK]' : '[...]'}
              </span>
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center gap-6 text-lg md:text-xl">
            <span className="text-marine-slate font-bold">PROGRESS:</span>
            <span className="text-marine-cyan font-mono tracking-wider text-xl">[{progressBar}]</span>
            <span className="text-marine-green font-bold text-2xl">{progress}%</span>
          </div>
        </div>

        {/* ASCII Logo */}
        {showLogo && (
          <div className="animate-flicker">
            <pre className="text-marine-cyan text-sm md:text-base lg:text-lg leading-tight overflow-x-auto text-glow-cyan font-bold">
              {ASCII_LOGO}
            </pre>
          </div>
        )}

        {/* Countdown */}
        {bootComplete && (
          <div className="mt-8 text-center">
            <p className="text-marine-amber text-xl md:text-2xl font-bold animate-pulse">
              Redirection vers le tableau de bord dans {countdown}...
            </p>
            {morseTriggered && (
              <p className="text-marine-green text-sm mt-2">
                Signal morse transmis
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BootSequence;
